import { Center, Grid, Group, Radio } from '@mantine/core';
import { useForm } from '@mantine/form';
import { TFunction } from 'i18next';
import { useMask } from '@react-input/mask';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Modal, Select, Text, TextInput } from 'components';
import { AuthorityConstants, ServiceConstants } from 'constant';
import { IRootState } from 'config/redux';
import { UserService } from 'services';
import { ProfileResponse, UserRequest, UserResponse } from 'types';
import { AppUtils, UserUtils } from 'utils';
import { MASK_SWISS_PHONE_NUMBER } from 'constant/app';

interface UserFormProps {
  error?: string;
  item: UserRequest;
  profiles: ProfileResponse[];
  loading?: boolean;
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: UserRequest) => void;
  t: TFunction;
  title?: string;
}

const { ROLE_DASH_ADMIN, ROLE_DASH_AGENT } = AuthorityConstants;
const { REQUEST_PENDING, REQUEST_USER_AGENTS_LIST_ID } = ServiceConstants;

const UserForm = ({ error, item, loading, opened, onClose, onSubmit, t, title, profiles }: UserFormProps) => {
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });
  const teamLeadServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USER_AGENTS_LIST_ID],
  );
  const [teamLeadOptions, setTeamLeadOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (opened) form.setValues(item);
    else form.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, item])

  const form = useForm({
    initialValues: item,
    validate: {
      firstName: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      lastName: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      countryCode: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      phoneNumber: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      profile: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      email: (value: any) => AppUtils.isEmpty(value) || !AppUtils.isEmailValid(value) ? t('error.required') : null,
    },
  });

  const canAssignTeamLead = UserUtils.hasAuthority(ROLE_DASH_ADMIN);
  const isAgentProfileSelected = useMemo(
    () => UserUtils.hasAuthority(ROLE_DASH_AGENT, { profile: form.values.profile } as UserResponse),
    [form.values.profile],
  );
  const shouldShowTeamLeadField = Boolean(item.id) && canAssignTeamLead && isAgentProfileSelected;
  const isTeamLeadLoading = useMemo(
    () => teamLeadServiceObject && teamLeadServiceObject.status === REQUEST_PENDING,
    [teamLeadServiceObject],
  );

  useEffect(() => {
    if (!opened || !shouldShowTeamLeadField) {
      return;
    }

    UserService.usersAgentsAll({ page: 0, pageSize: 200, search: '' });
  }, [opened, shouldShowTeamLeadField]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      teamLeadServiceObject,
      REQUEST_USER_AGENTS_LIST_ID,
      (data) => {
        const users = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
            ? data
            : [];

        setTeamLeadOptions(
          users
            .filter((user: UserResponse) => user.id !== item.id)
            .map((user: UserResponse) => ({
              value: String(user.id),
              label: user.fullName,
            })),
        );
      },
      () => setTeamLeadOptions([]),
    );
  }, [item.id, teamLeadServiceObject]);

  useEffect(() => {
    if (!isAgentProfileSelected && form.values.teamLeadId) {
      form.setFieldValue('teamLeadId', null);
    }
  }, [form, form.values.teamLeadId, isAgentProfileSelected]);

  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      {error && <Text c={'var(--mantine-color-error)'} mb={'md'}>{error}</Text>}
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label={t('user.firstName.label')}
            key={form.key('firstName')}
            {...form.getInputProps('firstName')}
            required={true}
            disabled={loading}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t('user.lastName.label')}
            key={form.key('lastName')}
            {...form.getInputProps('lastName')}
            required={true}
            disabled={loading}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            leftSection={<Text size={'xs'} pt={2}>{item.countryCode}</Text>}
            label={t('user.phone.label')}
            key={form.key('phoneNumber')}
            {...form.getInputProps('phoneNumber')}
            required={true}
            disabled={loading}
            ref={phoneNumberRef}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t('user.email.label')}
            key={form.key('email')}
            {...form.getInputProps('email')}
            required={true}
            disabled={loading || !!item.id}
          />
        </Grid.Col>
      </Grid>
      {shouldShowTeamLeadField && (
        <Select
          label={t('user.teamLead.label')}
          placeholder={t('user.teamLead.placeholder')}
          data={teamLeadOptions}
          value={form.values.teamLeadId ? String(form.values.teamLeadId) : null}
          onChange={(value) => form.setFieldValue('teamLeadId', value ? Number(value) : null)}
          searchable
          clearable
          disabled={loading || isTeamLeadLoading}
          mt={'sm'}
        />
      )}
      <Radio.Group
        name={'profile'}
        label={t('user.profile.label')}
        withAsterisk={true}
        defaultValue={item.profile?.code}
        mt={'sm'}
      >
        <Group mt={4}>
          {profiles.map(el => el.code !== 'CLIENT' &&
            <Radio
              key={el.code}
              label={el.name}
              value={el.code}
              disabled={loading}
              onChange={() => {
                const selected = profiles.find(item => item.code === el.code);
                if (selected) form.setFieldValue('profile', selected)
              }}
            />
          )}
        </Group>
      </Radio.Group>
      {item.id && item.status !== 'NOT_VERIFIED' &&
        <Select
          label={t('user.status.label')}
          data={[
            { value: 'ACTIVE', label: t('user.status.ACTIVE') },
            { value: 'SUSPENDED', label: t('user.status.SUSPENDED') }
          ]}
          key={form.key('status')}
          {...form.getInputProps('status')}
          mt={'sm'}
        />
      }
      <Center mt={'xl'}>
        <Button
          disabled={loading || !form.isValid()}
          loading={loading}
          onClick={() => onSubmit(form.getTransformedValues())}>
            {t('action.submit')}
        </Button>
      </Center>
    </Modal>
  )
}

export default UserForm;
